import React, { createContext, useContext, useState, useEffect } from 'react';
import {Navigate} from "react-router-dom";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [accessToken, setAccessToken] = useState(null);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);

    const login = async (username, password) => {
        if (username.trim() === '' || password.length < 4) {
            return { error: 'Please, use correct login, pass' };
        }
        try {
            const res = await fetch('/api/login', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                credentials: 'include',
                body: JSON.stringify({username, password})
            });
            const data = await res.json();
            if (res.ok) {
                setAccessToken(data.accessToken);
                await fetchUser(data.accessToken);
            }
            return data;
        }
        catch (err) {
            console.error('Login error:', err);
            return { error: 'server problems' };
        }
    };

    const register = async (username, password) => {
        if (username.trim() === '' || password.length < 4) {
            return { error: 'Please, use correct login, pass' };
        }
        try {
            const res = await fetch('/api/register', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                credentials: 'include',
                body: JSON.stringify({username, password})
            });
            const data = await res.json();
            if (res.ok) {
                setAccessToken(data.accessToken);
                await fetchUser(data.accessToken);
            }
            return data;
        } catch (err) {
            console.error('Registration error:', err);
            return { error: 'server problems' };
        }
    };

    const logout = async () => {
        try {
            await fetch('/api/logout', {
                method: 'POST',
                credentials: 'include'
            });
        } catch (err) {
            console.warn('Login error');
        }
        setAccessToken(null);
        setUser(null);
    };

    const fetchUser = async (token) => {
        try {
            const res = await fetch('/api/user', {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            const data = await res.json();
            if (res.ok) {
                setUser(data);
            } else {
                setUser(null);
            }
        } catch {
            setUser(null);
        }
    };

    const reloadUser = () => fetchUser(accessToken);

    const refresh = async () => {
        try {
            const res = await fetch('/api/refresh', {
                method: 'POST',
                credentials: 'include'
            });
            const data = await res.json();
            if (res.ok) {
                setAccessToken(data.accessToken);
                // ⬇ Получаем пользователя
                await fetchUser(data.accessToken);
            } else {
                setAccessToken(null);
                setUser(null);
            }
        } catch (err) {
            setAccessToken(null);
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    // ✅ Добавляем fetchWithAuth
    const fetchWithAuth = async (url, options = {}) => {
        if (!accessToken) return null;

        let res = await fetch(url, {
            ...options,
            headers: {
                ...options.headers,
                Authorization: `Bearer ${accessToken}`
            }
        });

        if (res.status === 401) {
            const refreshRes = await fetch('/api/refresh', {
                method: 'POST',
                credentials: 'include'
            });
            const refreshData = await refreshRes.json();

            if (refreshRes.ok && refreshData.accessToken) {
                setAccessToken(refreshData.accessToken);
                res = await fetch(url, {
                    ...options,
                    headers: {
                        ...options.headers,
                        Authorization: `Bearer ${refreshData.accessToken}`
                    }
                });
            } else {
                logout();
                window.location.href = '/';
                return null;
            }
        }

        return res;
    };

    useEffect(() => {
        refresh();
    }, []);

    useEffect(() => {
        if (!accessToken) return undefined;

        const pingPresence = async () => {
            try {
                await fetchWithAuth('/api/presence/ping', { method: 'POST' });
            } catch (err) {
                console.warn('Presence ping failed');
            }
        };

        pingPresence();
        const timer = setInterval(pingPresence, 30000);

        return () => clearInterval(timer);
    }, [accessToken]);

    return (
        <AuthContext.Provider value={{ accessToken, login, logout, register, loading, user, fetchWithAuth, reloadUser }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
